import React, { useContext, useState, useEffect, useRef } from "react";
import ReunionSquelette from "../layouts/ReunionSquelette";
import FooterMeetVideo from "../components/MeetVideoComponents/FooterMeetVideo";
import FooterMeetVideoMobile from "../components/MeetVideoComponents/FooterMeetVideoMobile";
import "../assets/css/meet-video-page.css";
import { AuthContext } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import MainMeetingContainer from "../components/MeetVideoComponents/MainMeetingContainer";
import OffcanvasMeeting from "../components/MeetVideoComponents/OffcanvasMeeting";
import { socket } from "../services/socket";
import MeetingContainer from "../components/MeetVideoComponents/MeetingContainer";
import { useMeeting } from "../hooks/useMeeting";
import { useMediasoupClient } from "../hooks/useMediasoupClient";
import { cleanupMediasoupConnection, reconnectTransports } from "../services/mediasoup-client";
import { upsertParticipant } from "../services/web-RTC";

export default function ReunionPage() {
  const {
    localStream,
    myPeer,
    participants,
    setParticipants,
    meetingId,
    accessCode,
    constraints,
    setConstraints,
    setLocalStream,
    setMyPeer,
    endMeetingSession,
    isReconnecting,
    startReconnection,
    endReconnection,
  } = useMeeting();
  const { user } = useContext(AuthContext);
  const {
    sendTransport,
    recvTransport,
    producerAudio,
    producerVideo,
    screenStream,
    setSendTransport,
    setRecvTransport,
    setProducerAudio,
    setProducerVideo,
    setIsScreenSharing,
    setScreenStream,
  } = useMediasoupClient();
  const location = useNavigate();
  const [display, setDisplay] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState(false);
  const [raisedHands, setRaisedHands] = useState([]);
  const [nouveauParticipant, setNouveauParticipant] = useState(null);
  const [msgNewPeer, setMsgNewPeer] = useState("");
  const [degradedUserIds, setDegradedUserIds] = useState([]);
  const [showCodeOrLink, setShowCodeOrLink] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);

  const localStreamRef = useRef(localStream);
  localStreamRef.current = localStream;

  const mediasoupRef = useRef({
    sendTransport, recvTransport, producerAudio, producerVideo, screenStream,
  });
  mediasoupRef.current = {
    sendTransport, recvTransport, producerAudio, producerVideo, screenStream,
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Nettoyage du flux local au démontage (navigation vers une autre page)
  useEffect(() => {
    return () => {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        setLocalStream(null);
      }
    };
  }, []);

  // Reconnexion meeting : quand le socket s'est reconnexion pendant une réunion active
  const isReconnectingRef = useRef(false);
  useEffect(() => {
    const handleSocketReconnect = async () => {
      if (!meetingId || !user || isReconnectingRef.current) return;

      isReconnectingRef.current = true;
      console.log("Socket réconnecté — reconnexion à la réunion en cours...");

      try {
        await reconnectTransports({
          user,
          meetingId,
          accessCode,
          ...mediasoupRef.current,
          localStream: localStreamRef.current,
          setSendTransport,
          setRecvTransport,
          setProducerAudio,
          setProducerVideo,
          setIsScreenSharing,
          setScreenStream,
          setParticipants,
          startReconnection,
          endReconnection,
        });
      } catch (err) {
        console.error("Erreur reconnexion meeting:", err);
      } finally {
        isReconnectingRef.current = false;
      }
    };

    socket.on("connect", handleSocketReconnect);
    return () => {
      socket.off("connect", handleSocketReconnect);
    };
  }, [
    meetingId,
    user,
    accessCode,
    setSendTransport,
    setRecvTransport,
    setProducerAudio,
    setProducerVideo,
    setIsScreenSharing,
    setScreenStream,
    setParticipants,
    startReconnection,
    endReconnection,
  ]);

  const toggleRaiseHand = () => {
    const alreadyRaised = raisedHands.find((p) => p.userId === user.userId);
    socket.emit("toggle_raise_hand", {
      meetingId,
      ...user,
      username: participants.find((peer) => peer.userId === user.userId)
        .displayName,
      action: alreadyRaised ? "lower" : "raise",
    });
  };

  useEffect(() => {
    if (myPeer) upsertParticipant(setParticipants, user.userId, myPeer);

    // Les participants savent que x lève ou baisse la main
    socket.on("raised_hands_updated", (handsList) => {
      setRaisedHands(handsList);
    });

    socket.on("participant_left", (peer) => {
      const { displayName, userId } = peer;
      setMsgNewPeer(`${displayName} a quitté la réunion`);
      setNouveauParticipant({
        image: peer.userImage,
        nom: peer.displayName.split(" ")[1],
        prenom: peer.displayName.split(" ")[0],
        userId: peer.userId,
        colors: peer.colors,
        exit: true,
      });
      setParticipants((prev) => {
        const existingPeer = prev.find((p) => p.userId === userId);
        if (existingPeer?.stream) {
          existingPeer.stream.getTracks().forEach((track) => track.stop());
        }
        return prev.filter((p) => p.userId !== userId);
      });
      setDegradedUserIds((prev) => prev.filter((id) => id !== userId));
    });

    // Gestion de la déconnexion temporaire d'un peer
    socket.on("peer_temporarily_disconnected", (peer) => {
      setMsgNewPeer(`${peer.displayName} a des problèmes de connexion`);
      setNouveauParticipant({
        image: null,
        nom: peer.displayName,
        prenom: "",
        userId: peer.userId,
        colors: null,
        exit: false,
        temporary: true,
      });
    });
    socket.on("peer_degraded", (peer) => {
      setMsgNewPeer(`${peer.displayName || peer.userId} a des problèmes de connexion`);
      setNouveauParticipant({
        image: null,
        nom: peer.displayName || "",
        prenom: "",
        userId: peer.userId,
        colors: null,
        exit: false,
        temporary: true,
      });
      setDegradedUserIds((prev) =>
        prev.includes(peer.userId) ? prev : [...prev, peer.userId]
      );
    });


    // Peer reconnecté avec succès
    socket.on("peer_reconnected", (peer) => {
      setMsgNewPeer(`${peer.displayName} est de retour dans la réunion`);
      setNouveauParticipant({
        image: peer.userImage,
        nom: peer.displayName.split(" ")[1] || peer.displayName,
        prenom: peer.displayName.split(" ")[0] || "",
        userId: peer.userId,
        colors: null,
        exit: false,
        reconnect: true,
      });

      // Mettre à jour l'état des participants si fourni
      if (peer.participantsState) {
        peer.participantsState.forEach((state) => {
          upsertParticipant(setParticipants, state.userId, state);
        });
      }
      setDegradedUserIds((prev) => prev.filter((id) => id !== peer.userId));
    });

    // Peer en cours de reconnexion (information)
    socket.on("peer_reconnecting", (peer) => {
      setMsgNewPeer(`${peer.displayName} se reconnecte...`);
    });

    socket.on("receive_messages_meeting", (datas) => {
      const { messages, peers } = datas;
      const { timeUnix } = peers.find((peer) => peer.userId === user.userId);
      const msgs = messages.filter((msg) => msg.timeUnix > timeUnix);
      const myMessages = msgs.sort((a, b) => {
        return b.timeUnix - a.timeUnix;
      });
      setNewMessage(true);
      setMessages(myMessages);
    });

    socket.on("reunion_terminee_pour_moi", () => {
      cleanupMediasoupConnection({
        sendTransport,
        recvTransport,
        producerVideo,
        producerAudio,
        localStream,
        screenStream,
      });
      setIsScreenSharing(false);
      setScreenStream(null);
      setSendTransport(null);
      setRecvTransport(null);
      setProducerAudio(null);
      setProducerVideo(null);
      endMeetingSession();
      location("/reunion");
    });

    return () => {
      socket.off("raised_hands_updated");
      socket.off("participant_left");
      socket.off("peer_temporarily_disconnected");
      socket.off("peer_reconnected");
      socket.off("peer_reconnecting");
      socket.off("receive_messages_meeting");
      socket.off("peer_degraded");

      socket.off("reunion_terminee_pour_moi");
      socket.off("finish_init_meeting");
      socket.off("nouvelle_participation");
      socket.off("fin_meeting_from_user");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, isReconnecting]);

  // E1 client : heartbeat applicatif toutes les 5s (page active / visibilité)
  useEffect(() => {
    if (!meetingId || !user) return undefined;
    const emitHeartbeat = () => {
      socket.emit("meeting_heartbeat", {
        meetingId,
        userId: user.userId,
        visible: typeof document !== "undefined" && document.visibilityState !== "hidden",
      });
    };
    emitHeartbeat();
    const id = setInterval(emitHeartbeat, 5000);
    const onVisibility = () => emitHeartbeat();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    return () => {
      clearInterval(id);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, [meetingId, user?.userId]);
  // E6 : reconnexion proactive (online/offline + visibilitychange)
  useEffect(() => {
    if (!meetingId || !user) return undefined;
    const isOnline = () => typeof navigator !== "undefined" ? navigator.onLine : true;
    const tryReconnect = () => {
      if (!isOnline()) return;
      if (isReconnectingRef && isReconnectingRef.current) return;
      if (socket && socket.connected) return;
      try {
        socket.reconnect();
      } catch (e) {}
    };
    window.addEventListener("online", tryReconnect);
    document.addEventListener("visibilitychange", tryReconnect);
    return () => {
      window.removeEventListener("online", tryReconnect);
      document.removeEventListener("visibilitychange", tryReconnect);
    };
  }, [meetingId, user?.userId]);

  return (
    <ReunionSquelette>
      <MainMeetingContainer>
        {localStream || accessCode ? (
          <>
            <MeetingContainer
              mediasTools={{
                showCodeOrLink,
                setShowCodeOrLink,
                nombreParticipants: participants.length,
                stream: localStream,
                setIsMuted,
                isMuted,
                nouveauParticipant,
                setNouveauParticipant,
                msgNewPeer,
                setMsgNewPeer,
                accessCode,
                raisedHands,
                degradedUserIds,
              }}
            />
            <OffcanvasMeeting
              messages={messages}
              setMessages={setMessages}
              meetingId={meetingId}
              accessCode={accessCode}
              display={display}
              setDisplay={setDisplay}
              newMessage={newMessage}
              setNewMessage={setNewMessage}
            />
          </>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "90vh",
            }}
          >
            <button
              onClick={() => location("/reunion")}
              style={{
                height: "50px",
                width: "250px",
                borderRadius: "25px",
                border: "2px solid #fff",
                margin: "auto",
              }}
              className="bg-transparent animate__animated animate__pulse animate__infinite fs-3"
            >
              Retour en arrière
            </button>
          </div>
        )}
      </MainMeetingContainer>

      {localStream || accessCode ? (
        isMobile ? (
          <FooterMeetVideoMobile
            toggleRaiseHand={toggleRaiseHand}
            raisedHands={raisedHands}
            newMessage={newMessage}
            setNewMessage={setNewMessage}
            display={display}
            setDisplay={setDisplay}
            accessCode={accessCode}
            mediasTools={{
              setShowCodeOrLink,
              constraints,
              meetingId,
              setConstraints,
              isMuted,
              setIsMuted,
            }}
          />
        ) : (
          <FooterMeetVideo
            toggleRaiseHand={toggleRaiseHand}
            raisedHands={raisedHands}
            newMessage={newMessage}
            setNewMessage={setNewMessage}
            display={display}
            setDisplay={setDisplay}
            accessCode={accessCode}
            mediasTools={{
              setShowCodeOrLink,
              constraints,
              meetingId,
              setConstraints,
              isMuted,
              setIsMuted,
            }}
          />
        )
      ) : (
        <></>
      )}
    </ReunionSquelette>
  );
}
